import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Drawer,
  MODEL_LABEL,
  Skeleton,
  StatusPill,
  accessKey as formatKey,
  dateTime,
  money,
  time,
} from '@fa/ui';
import { ApiError, api, openDocumentFile } from './lib/api.js';
import type { DocumentView } from './lib/types.js';

/**
 * Gaveta de detalhe da nota.
 *
 * E aqui que a rejeicao vira algo acionavel: a mensagem traduzida, o campo
 * exato a corrigir e a linha do tempo do que aconteceu. Sem isso o operador
 * ficaria com "Rejeicao 778" e um telefone para ligar.
 */
export function useDocumentDrawer(): { drawer: ReactNode; open: (id: string) => void } {
  const [documentId, setDocumentId] = useState<string | null>(null);

  const drawer = <DocumentDrawer documentId={documentId} onClose={() => setDocumentId(null)} />;

  return { drawer, open: setDocumentId };
}

function DocumentDrawer({
  documentId,
  onClose,
}: {
  documentId: string | null;
  onClose: () => void;
}): ReactNode {
  const queryClient = useQueryClient();
  const [justification, setJustification] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['document', documentId],
    queryFn: () => api<DocumentView>(`/v1/documents/${documentId as string}`),
    enabled: documentId !== null,
    // Nota em processamento muda sozinha; a gaveta acompanha.
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      return status === 'queued' || status === 'processing' ? 3000 : false;
    },
  });

  const cancel = useMutation({
    mutationFn: () =>
      api(`/v1/documents/${documentId as string}/cancel`, {
        method: 'POST',
        body: { justification },
      }),
    onSuccess: () => {
      setCancelling(false);
      setJustification('');
      setFeedback('Nota cancelada. O evento foi registrado na SEFAZ.');
      void queryClient.invalidateQueries({ queryKey: ['document', documentId] });
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
      void queryClient.invalidateQueries({ queryKey: ['overview'] });
    },
    onError: (err) => {
      setFeedback(err instanceof ApiError ? err.message : 'Nao foi possivel cancelar.');
    },
  });

  const doc = query.data;
  const open = documentId !== null;

  const close = (): void => {
    setCancelling(false);
    setJustification('');
    setFeedback(null);
    onClose();
  };

  return (
    <Drawer
      open={open}
      title={doc ? `${MODEL_LABEL[doc.model]} nº ${doc.number.toLocaleString('pt-BR')}` : 'Carregando...'}
      subtitle={doc ? `Série ${doc.serie}${doc.reference ? ` · ${doc.reference}` : ''}` : undefined}
      onClose={close}
      footer={
        doc && (
          <>
            {doc.files.xml && (
              <button className="btn btn-ghost" onClick={() => void openDocumentFile(doc.id, 'xml')}>
                Baixar XML
              </button>
            )}
            {doc.files.pdf && (
              <button className="btn btn-ghost" onClick={() => void openDocumentFile(doc.id, 'pdf')}>
                {doc.model === 'nfce' ? 'Baixar cupom' : 'Baixar DANFE'}
              </button>
            )}
            {doc.status === 'authorized' && !cancelling && (
              <button className="btn btn-danger" onClick={() => setCancelling(true)}>
                Cancelar nota
              </button>
            )}
          </>
        )
      }
    >
      {query.isLoading && <Skeleton rows={6} />}

      {doc && (
        <>
          {doc.sefaz && (doc.status === 'rejected' || doc.status === 'denied' || doc.status === 'error') && (
            <div className="alert bad">
              <b>
                {doc.status === 'denied' ? 'Uso denegado' : 'Rejeição'} {doc.sefaz.code}
              </b>
              {doc.sefaz.message ?? doc.sefaz.message_raw}
              {doc.sefaz.field && (
                <div className="fix">
                  Campo a corrigir: <span className="code-inline">{doc.sefaz.field}</span>
                </div>
              )}
            </div>
          )}

          {doc.status === 'contingency' && (
            <div className="alert warn">
              <b>Emitida em contingência offline</b>A SEFAZ estava indisponível. O cupom foi impresso com QR
              Code de contingência e será transmitido automaticamente assim que o serviço voltar.
              {doc.contingency && (
                <div className="fix">Prazo legal de transmissão: {dateTime(doc.contingency.deadline)}</div>
              )}
            </div>
          )}

          {feedback && (
            <div className="alert info">
              <b>Resultado</b>
              {feedback}
            </div>
          )}

          {cancelling && (
            <div className="alert warn">
              <b>Cancelar esta nota</b>
              A SEFAZ exige uma justificativa de no mínimo 15 caracteres, que fica registrada no evento de
              cancelamento.
              <textarea
                className="input"
                style={{ width: '100%', height: 70, marginTop: 10, padding: 8, minWidth: 0 }}
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                placeholder="Ex.: Cliente desistiu da compra apos a emissao do cupom"
              />
              <div className="fix">
                <button
                  className="btn btn-sm btn-danger"
                  disabled={justification.trim().length < 15 || cancel.isPending}
                  onClick={() => cancel.mutate()}
                >
                  {cancel.isPending ? 'Cancelando...' : 'Confirmar cancelamento'}
                </button>
                <button className="btn btn-sm btn-ghost" onClick={() => setCancelling(false)}>
                  Voltar
                </button>
              </div>
            </div>
          )}

          <dl className="kv">
            <dt>Situação</dt>
            <dd>
              <StatusPill status={doc.status} />
            </dd>
            <dt>Valor</dt>
            <dd className="num">{money(doc.totals?.total ?? null)}</dd>
            <dt>Chave de acesso</dt>
            <dd className="key">{doc.access_key ? formatKey(doc.access_key) : '—'}</dd>
            <dt>Protocolo</dt>
            <dd>{doc.protocol ?? (doc.status === 'contingency' ? 'aguardando transmissão' : '—')}</dd>
            <dt>Ambiente</dt>
            <dd>{doc.environment === 'production' ? 'Produção' : 'Homologação (sandbox)'}</dd>
            <dt>Emissão</dt>
            <dd>{doc.emission_type === 9 ? 'Contingência offline' : 'Normal'}</dd>
          </dl>

          {doc.totals && (doc.totals.ibs > 0 || doc.totals.cbs > 0) && (
            <div>
              <h3 style={{ fontSize: '.95rem', marginBottom: 10 }}>Reforma tributária (informativo)</h3>
              <dl className="kv">
                <dt>IBS</dt>
                <dd className="num">{money(doc.totals.ibs)}</dd>
                <dt>CBS</dt>
                <dd className="num">{money(doc.totals.cbs)}</dd>
              </dl>
            </div>
          )}

          <div>
            <h3 style={{ fontSize: '.95rem', marginBottom: 10 }}>Linha do tempo</h3>
            <ul className="timeline">
              <li>
                <span />
                Recebida pela API
                <time>{time(doc.timestamps.created_at)}</time>
              </li>
              {doc.status !== 'queued' && (
                <li>
                  <span />
                  Tributos calculados e XML assinado
                  <time>{time(doc.timestamps.created_at)}</time>
                </li>
              )}
              {doc.status === 'contingency' && (
                <li className="bad">
                  <span />
                  SEFAZ indisponível · assinada em contingência
                  <time>{time(doc.timestamps.created_at)}</time>
                </li>
              )}
              {doc.timestamps.processed_at && (
                <li className={doc.status === 'authorized' ? '' : 'bad'}>
                  <span />
                  {doc.status === 'authorized'
                    ? `Autorizada · protocolo ${doc.protocol ?? ''}`
                    : `SEFAZ respondeu ${doc.sefaz?.code ?? ''}`}
                  <time>{time(doc.timestamps.processed_at)}</time>
                </li>
              )}
              {(doc.events ?? []).map((event) => (
                <li key={event.id} className={event.type === 'error' ? 'bad' : ''}>
                  <span />
                  {EVENT_LABEL[event.type] ?? event.type}
                  {event.protocol ? ` · protocolo ${event.protocol}` : ''}
                  <time>{time(event.created_at)}</time>
                </li>
              ))}
              {(doc.status === 'queued' || doc.status === 'processing') && (
                <li className="mute">
                  <span />
                  Na fila de transmissão
                  <time>—</time>
                </li>
              )}
            </ul>
          </div>
        </>
      )}
    </Drawer>
  );
}

const EVENT_LABEL: Record<string, string> = {
  cancel: 'Cancelamento registrado',
  correction: 'Carta de correção registrada',
  contingency: 'Entrada em contingência',
  error: 'Falha no processamento',
};
